import React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/shared/ui/feedbacks/modal';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import type {
  InventoryResultArgs,
  InventoryCandidateArgs
} from '@this/src/api/domestic_air/same_day_inventory_checks_api';

export type SameDayInventoryPhase =
  | 'checking' // (1) 在庫確認中
  | 'price_increased' // (2) 価格上昇
  | 'ticket_type_changed' // (3) 券種変更
  | 'unavailable' // (4) 空席なし
  | 'error'; // (5) エラー（stale等）

export type SameDayFlightInfoArgs = {
  route: string; // 例: 伊丹空港(大阪) → 羽田空港(東京)
  departureDate: string; // 例: 2026/06/12
  timeRange: string; // 例: 10:30発 → 11:30着
  flightNumber: string; // 例: ANA565
};

export type SameDayOriginalTicketArgs = {
  seatType?: string;
  ticketType?: string;
};

export type SameDayInventoryCheckModalProps = {
  open: boolean;
  phase: SameDayInventoryPhase;
  results: InventoryResultArgs[];
  flightInfos?: Record<number, SameDayFlightInfoArgs>;
  originalTickets?: Record<number, SameDayOriginalTicketArgs>;
  errorMessage?: string;
  submitting?: boolean;
  onProceed: () => void;
  onBackToSelect: () => void;
};

const formatPrice = (value: number): string => `${value.toLocaleString()}円`;

const cheapestCandidate = (candidates: InventoryCandidateArgs[]): InventoryCandidateArgs | undefined => {
  if (candidates.length === 0) return undefined;
  return candidates.reduce((min, candidate) => (candidate.price < min.price ? candidate : min));
};

const badgeStyle: React.CSSProperties = {
  backgroundColor: '#F6EAE6',
  borderRadius: '20px',
  padding: '7px 14px',
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#A72C09'
};

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => <div style={badgeStyle}>{children}</div>;

const diffPillStyle: React.CSSProperties = {
  backgroundColor: '#F6EAE6',
  borderRadius: '10px',
  padding: '2px 7px',
  fontSize: '11px',
  fontWeight: 500,
  color: '#A72C09'
};

const DiffPill: React.FC<{ diff: number }> = ({ diff }) => (
  <span style={diffPillStyle}>＋{diff.toLocaleString()}円</span>
);

const ComparisonTableHeader: React.FC = () => (
  <div
    style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 14px', backgroundColor: '#F8F8F8' }}
  >
    <div style={{ flex: 1 }} />
    <div style={{ width: '150px', fontSize: '12px', color: '#969696', textAlign: 'right' }}>選択時</div>
    <div style={{ width: '150px', fontSize: '12px', color: '#969696', textAlign: 'right' }}>現在</div>
  </div>
);

const ComparisonTableRow: React.FC<{
  label: string;
  selectedValue: React.ReactNode;
  currentValue: React.ReactNode;
  emphasizeCurrent?: boolean;
}> = ({ label, selectedValue, currentValue, emphasizeCurrent }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px 14px',
      borderTop: '1px solid #F8F8F8'
    }}
  >
    <div style={{ flex: 1, fontSize: '13px', color: '#959595' }}>{label}</div>
    <div style={{ width: '150px', textAlign: 'right', fontSize: '13px', color: '#999999' }}>{selectedValue}</div>
    <div
      style={{
        width: '150px',
        textAlign: 'right',
        fontSize: '13px',
        color: '#212121',
        fontWeight: emphasizeCurrent ? 'bold' : 500
      }}
    >
      {currentValue}
    </div>
  </div>
);

const FlightInfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '8px', padding: '10px 14px' }}>
    <div style={{ width: '80px', fontSize: '12px', color: '#959595' }}>{label}</div>
    <div style={{ fontSize: '13px', color: '#4D4D4D' }}>{value}</div>
  </div>
);

const FlightInfoSection: React.FC<{ flightInfo: SameDayFlightInfoArgs }> = ({ flightInfo }) => (
  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <div style={{ fontSize: '12px', fontWeight: 500, color: '#808080' }}>選択内容</div>
    <div style={{ border: '1px solid #F8F8F8', borderRadius: '6px' }}>
      <FlightInfoRow label="区間" value={flightInfo.route} />
      <FlightInfoRow label="出発日" value={flightInfo.departureDate} />
      <FlightInfoRow label="時間" value={flightInfo.timeRange} />
      <FlightInfoRow label="便名" value={flightInfo.flightNumber} />
    </div>
  </div>
);

const SimpleFlightInfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '12px', marginBottom: '6px' }}>
    <div style={{ width: '72px', fontSize: '13px', color: '#959595' }}>{label}</div>
    <div style={{ fontSize: '13px', color: '#4D4D4D' }}>{value}</div>
  </div>
);

const InventoryCautionAlert: React.FC = () => (
  <Alert variant="warning" style={{ width: '100%' }}>
    <span style={{ fontSize: '11px', lineHeight: 1.5 }}>
      表示内容は現時点での空席結果であり、ご予約を確約するものではございません。
      <br />
      ご依頼受付後、予約の可否と詳細についてオペレーターより順次ご連絡いたします。
      <br />
      手配状況は予約詳細画面からご確認ください。
    </span>
  </Alert>
);

type ComparisonStatus = 'price_increased' | 'ticket_type_changed';

// 混在依頼（券種変更＋価格上昇）でも全対象アイテムを表示するための絞り込み
const isComparisonResult = (
  result: InventoryResultArgs
): result is InventoryResultArgs & { inventoryStatus: ComparisonStatus } =>
  result.inventoryStatus === 'price_increased' || result.inventoryStatus === 'ticket_type_changed';

const ComparisonTable: React.FC<{
  phase: ComparisonStatus;
  result: InventoryResultArgs;
  originalTicket?: SameDayOriginalTicketArgs;
}> = ({ phase, result, originalTicket }) => {
  const candidate = phase === 'ticket_type_changed' ? cheapestCandidate(result.candidates) : undefined;
  const hasCandidate = phase === 'price_increased' || candidate !== undefined;

  const currentTicketType =
    phase === 'price_increased' ? originalTicket?.ticketType ?? '-' : candidate?.ticketType ?? '-';
  const currentSeatType =
    phase === 'price_increased' ? originalTicket?.seatType ?? '-' : candidate?.seatType ?? '-';

  const newPrice = phase === 'price_increased' ? result.newPrice : candidate?.price ?? null;
  const priceDiff =
    phase === 'price_increased'
      ? result.priceDiff
      : candidate != null && result.originalPrice != null
      ? candidate.price - result.originalPrice
      : null;

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: '#fff',
        border: '1px solid #F8F8F8',
        borderRadius: '6px',
        overflow: 'hidden'
      }}
    >
      <ComparisonTableHeader />
      <ComparisonTableRow
        label="券種"
        selectedValue={originalTicket?.ticketType ?? '-'}
        currentValue={currentTicketType}
        emphasizeCurrent={phase === 'ticket_type_changed'}
      />
      <ComparisonTableRow
        label="座席"
        selectedValue={originalTicket?.seatType ?? '-'}
        currentValue={currentSeatType}
      />
      <ComparisonTableRow
        label="料金"
        selectedValue={result.originalPrice != null ? formatPrice(result.originalPrice) : '-'}
        currentValue={
          hasCandidate ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', color: '#212121' }}>
                {newPrice != null ? formatPrice(newPrice) : '-'}
              </span>
              {priceDiff != null && priceDiff > 0 && <DiffPill diff={priceDiff} />}
            </div>
          ) : (
            '代替候補なし'
          )
        }
      />
    </div>
  );
};

const CheckingModal: React.FC<{ open: boolean }> = ({ open }) => (
  <Modal open={open} size="medium" disableEscapeKeyDown>
    <ModalHeader>最新の空席状況を確認中です</ModalHeader>
    <ModalBody style={{ padding: '32px 24px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        <Loading size="medium" />
        <div style={{ fontSize: '14px', color: '#000', textAlign: 'center' }}>
          ブラウザの[戻る]操作をせずにお待ちください。
        </div>
        <div style={{ fontSize: '12px', color: '#8C8C8C', textAlign: 'center' }}>
          処理に1分程度かかる場合がございます。
        </div>
      </div>
    </ModalBody>
  </Modal>
);

const ComparisonModal: React.FC<{
  open: boolean;
  phase: ComparisonStatus;
  results: InventoryResultArgs[];
  flightInfos?: Record<number, SameDayFlightInfoArgs>;
  originalTickets?: Record<number, SameDayOriginalTicketArgs>;
  submitting?: boolean;
  onProceed: () => void;
  onBackToSelect: () => void;
}> = ({ open, phase, results, flightInfos, originalTickets, submitting, onProceed, onBackToSelect }) => {
  const targetResults = results.filter(isComparisonResult);
  const badgeText =
    phase === 'price_increased'
      ? '選択時より料金が値上がりしています'
      : 'ご希望の券種は満席のため、別の券種をご案内します';

  return (
    <Modal open={open} size="medium" disableEscapeKeyDown>
      <ModalHeader>残席を確認しました</ModalHeader>
      <ModalBody style={{ padding: '24px 24px 20px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <Badge>{badgeText}</Badge>
          <div style={{ fontSize: '13px', color: '#000' }}>このまま依頼を進めますか？</div>
          {targetResults.map(result => (
            <React.Fragment key={result.itemIndex}>
              <ComparisonTable
                phase={result.inventoryStatus}
                result={result}
                originalTicket={originalTickets?.[result.itemIndex]}
              />
              {flightInfos?.[result.itemIndex] && <FlightInfoSection flightInfo={flightInfos[result.itemIndex]} />}
            </React.Fragment>
          ))}
          <InventoryCautionAlert />
        </div>
      </ModalBody>
      <ModalFooter style={{ padding: '4px 24px 20px' }}>
        <Button color="sub" onClick={onBackToSelect} disabled={submitting} style={{ flex: 1 }}>
          キャンセルして検索に戻る
        </Button>
        <Button color="primary" onClick={onProceed} loading={submitting} disabled={submitting} style={{ flex: 1 }}>
          この内容で手配を依頼
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const UnavailableModal: React.FC<{
  open: boolean;
  results: InventoryResultArgs[];
  flightInfos?: Record<number, SameDayFlightInfoArgs>;
  onBackToSelect: () => void;
}> = ({ open, results, flightInfos, onBackToSelect }) => {
  const targetInfos = results
    .filter(r => r.inventoryStatus === 'unavailable')
    .map(result => ({ itemIndex: result.itemIndex, flightInfo: flightInfos?.[result.itemIndex] }))
    .filter((info): info is { itemIndex: number; flightInfo: SameDayFlightInfoArgs } => info.flightInfo != null);

  return (
    <Modal open={open} size="medium" disableEscapeKeyDown>
      <ModalHeader>空席がなくなりました</ModalHeader>
      <ModalBody style={{ padding: '28px 24px 20px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <Badge>ご希望の便は満席です</Badge>
          <div style={{ fontSize: '13px' }}>お手数ですが、検索画面より別の便を選択してください。</div>
          {targetInfos.length > 0 && (
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: '12px', fontWeight: 500, color: '#808080', marginBottom: '8px' }}>
                選択内容
              </div>
              {targetInfos.map(({ itemIndex, flightInfo }) => (
                <div key={itemIndex} style={{ marginBottom: '12px' }}>
                  <SimpleFlightInfoRow label="区間" value={flightInfo.route} />
                  <SimpleFlightInfoRow label="出発日" value={flightInfo.departureDate} />
                  <SimpleFlightInfoRow label="時間" value={flightInfo.timeRange} />
                  <SimpleFlightInfoRow label="便名" value={flightInfo.flightNumber} />
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter style={{ padding: '4px 24px 20px' }}>
        <Button color="primary" onClick={onBackToSelect} style={{ flex: 1 }}>
          検索結果に戻る
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// error フェーズ（既存実装の文言・構造を維持。Figma対象外）
const ErrorModal: React.FC<{ open: boolean; errorMessage?: string; onBackToSelect: () => void }> = ({
  open,
  errorMessage,
  onBackToSelect
}) => (
  <Modal open={open} size="small" disableEscapeKeyDown>
    <ModalHeader>在庫確認に失敗しました</ModalHeader>
    <ModalBody>
      <p style={{ margin: 0 }}>
        {errorMessage || '在庫確認に失敗しました。お手数ですが検索結果に戻ってやり直してください。'}
      </p>
    </ModalBody>
    <ModalFooter>
      <Button color="sub" onClick={onBackToSelect}>
        検索結果に戻る
      </Button>
    </ModalFooter>
  </Modal>
);

const SameDayInventoryCheckModal: React.FC<SameDayInventoryCheckModalProps> = ({
  open,
  phase,
  results,
  flightInfos,
  originalTickets,
  errorMessage,
  submitting,
  onProceed,
  onBackToSelect
}) => {
  if (phase === 'checking') {
    return <CheckingModal open={open} />;
  }

  if (phase === 'unavailable') {
    return (
      <UnavailableModal open={open} results={results} flightInfos={flightInfos} onBackToSelect={onBackToSelect} />
    );
  }

  if (phase === 'error') {
    return <ErrorModal open={open} errorMessage={errorMessage} onBackToSelect={onBackToSelect} />;
  }

  // phase === 'price_increased' | 'ticket_type_changed'
  return (
    <ComparisonModal
      open={open}
      phase={phase}
      results={results}
      flightInfos={flightInfos}
      originalTickets={originalTickets}
      submitting={submitting}
      onProceed={onProceed}
      onBackToSelect={onBackToSelect}
    />
  );
};

export default SameDayInventoryCheckModal;
